import { Component, OnInit, input, signal } from '@angular/core';
import { PatientTargetModel, GeneralAttachmentModel } from '../../../core/api/models';
import { DomSanitizer } from '@angular/platform-browser';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { SupervisorApiService } from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { SparkLightLoadService } from '../../../core/services/spark-light-load.service';

@Component({
    selector: 'app-target-info',
    standalone: false,
    templateUrl: './target-info.component.html',
    styleUrl: './target-info.component.scss'
})
export class TargetInfoComponent implements OnInit {
    target = input.required<PatientTargetModel>();
    isPreview = input<boolean>(false);
    
    showGraph = signal<boolean>(false);
    isEditingTargetNotes = signal<boolean>(false);
    isLoadingAttachments = signal<boolean>(false);
    doShowAttachmentView = signal<boolean>(false);
    attachmentList = signal<GeneralAttachmentModel[]>([]);
    selectedAttachment = signal<GeneralAttachmentModel | null>(null);
    isOfflineMode = signal<boolean>(false);
    editedTargetNotes: string = '';

    constructor(
        protected sanitizer: DomSanitizer,
        private supervisorApiService: SupervisorApiService,
        private staffApiService: StaffApiService,
        private alertService: AlertService,
        private offlineDataService: OfflineDataService,
        protected lightLoad: SparkLightLoadService
    ) {}

    ngOnInit(): void {
        this.isOfflineMode.set(this.offlineDataService.isInOfflineMode());

        const currentTarget = this.target();
        if (currentTarget.HasUnreadTargetNoteForStaff && !this.isPreview() && !this.lightLoad.isEnabled()) {
            currentTarget.HasUnreadTargetNoteForStaff = false;
            if (!this.isOfflineMode()) {
                this.staffApiService.MarkTargetNoteMessagesAsReadByStaff({
                    targetId: currentTarget.ID ?? 0
                }).subscribe({
                    error: (error) => {
                        currentTarget.HasUnreadTargetNoteForStaff = true;
                        console.error('Error marking target notes as read by staff', error);
                    }
                });
            }
        }
        
        if (!this.isOfflineMode() && !this.lightLoad.isEnabled()) {
            this.supervisorApiService.GetTargetAttachments({
                targetID: this.target().ID ?? 0
            }).subscribe({
                next: (results) => {
                    this.attachmentList.set(results);
                },
                error: (error) => {
                    console.error('Error loading target attachments', error);
                    this.alertService.error('Failed to load target attachments');
                }
            });
        }
    }

    onSeeGraphs(): void {
        this.showGraph.set(true);
    }

    onHideGraph(): void {
        this.showGraph.set(false);
    }

    startEditingTargetNotes(): void {
        this.editedTargetNotes = this.target().TargetNotes ?? '';
        this.isEditingTargetNotes.set(true);
    }

    cancelEditingTargetNotes(): void {
        this.editedTargetNotes = '';
        this.isEditingTargetNotes.set(false);
    }

    saveTargetNotes(): void {
        const currentTarget = this.target();
        this.staffApiService.ChangeTargetNotes({
            targetId: currentTarget.ID!,
            newNotes: this.editedTargetNotes,
            shouldMarkAsNew: true
        }).subscribe(() => {
            // Update the target object with the new notes
            currentTarget.TargetNotes = this.editedTargetNotes;
            this.isEditingTargetNotes.set(false);
        });
    }

    lastModifiedByText(): string {
        const target = this.target();
        if (!target?.NotesLastModifiedByUserFullName || !target?.NotesLastModifiedDate) {
            return '';
        }
        return 'Last modified by: ' + target.NotesLastModifiedByUserFullName + ' at ';
    }
  
    getImageUrl(att: GeneralAttachmentModel): string {
        return 'data:' + att.FileType + ';base64,' + att.ImageThumbnail;
    }
  
    openAttachmentView(att: GeneralAttachmentModel): void {
        this.doShowAttachmentView.set(true);
        this.selectedAttachment.set(att);
    }
  
    closeAttachmentView(): void {
        this.doShowAttachmentView.set(false);
        this.selectedAttachment.set(null);
        if (this.isOfflineMode() || this.lightLoad.isEnabled()) {
            return;
        }
        this.loadAttachments();
    }

    loadAttachments(): void {
        if (this.isOfflineMode() || this.lightLoad.isEnabled()) {
            return;
        }
        this.supervisorApiService.GetTargetAttachments({
            targetID: this.target().ID ?? 0
        }).subscribe({
            next: (results) => {
                this.attachmentList.set(results);
            },
            error: (error) => {
                console.error('Error loading target attachments', error);
                this.alertService.error('Failed to load target attachments');
            }
        });
    }
}

